import { createFileRoute, Link } from '@tanstack/react-router';
import {
    Check,
    CircleCheck,
    CircleX,
    Database,
    RefreshCw,
    ShieldCheck,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@apps/web/components/ui/command';
import { Input } from '@apps/web/components/ui/input';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import {
    useOrgSnapshotImportJob,
    useOrgSnapshotsStatus,
    useProdOrganizationSearch,
} from '@apps/web/swr_data_fetching/admin_sandbox_queries';
import { ApiError } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/org-snapshots')({
    component: OrgSnapshotsPage,
    head: () => ({
        meta: [
            {
                title: 'Org Snapshots | Phoebe Admin',
            },
        ],
    }),
});

type ProdOrganization = {
    readonly organization_id: string;
    readonly name: string;
    readonly internal_name: string | null;
    readonly caregiver_count: number;
    readonly shift_count: number;
};

type ImportJob = NonNullable<
    ReturnType<typeof useOrgSnapshotImportJob>['data']
>;

const PROGRESS_STAGES = [
    { label: 'Connect', threshold: 5 },
    { label: 'Export', threshold: 10 },
    { label: 'Import', threshold: 55 },
    { label: 'Audit', threshold: 95 },
] as const;

function formatCount(value: number) {
    return new Intl.NumberFormat().format(value);
}

function formatSnapshotTimestamp(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
}

function buildDefaultSnapshotName(organizationName: string) {
    return `${organizationName} Snapshot - ${formatSnapshotTimestamp(
        new Date(),
    )}`;
}

function statusVariant(status: ImportJob['status']) {
    if (status === 'completed') {
        return 'success';
    }
    if (status === 'failed') {
        return 'destructive';
    }
    return 'secondary';
}

function isTerminalImportStatus(status: ImportJob['status'] | undefined) {
    return status === 'completed' || status === 'failed';
}

function ImportProgressPanel({ job }: { readonly job: ImportJob | undefined }) {
    const tableCounts = useMemo(() => {
        if (!job?.result) {
            return [];
        }
        return Object.entries(job.result.table_row_counts).sort(
            ([left], [right]) => left.localeCompare(right),
        );
    }, [job?.result]);

    if (!job) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Database className="h-4 w-4" />
                        Import Progress
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-muted-foreground text-sm">
                        No active import job.
                    </div>
                </CardContent>
            </Card>
        );
    }

    const isRunning = job.status === 'running' || job.status === 'queued';
    const progressPercent = Math.max(0, Math.min(job.progress_percent, 100));
    const activeStageIndex = PROGRESS_STAGES.reduce(
        (activeIndex, stage, index) =>
            progressPercent >= stage.threshold ? index : activeIndex,
        0,
    );
    const activeStage = PROGRESS_STAGES[activeStageIndex] ?? PROGRESS_STAGES[0];

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <CardTitle className="flex items-center gap-2">
                        {isRunning ? (
                            <Spinner />
                        ) : job.status === 'completed' ? (
                            <ShieldCheck className="h-4 w-4 text-green-700 dark:text-green-400" />
                        ) : (
                            <CircleX className="h-4 w-4 text-red-700 dark:text-red-400" />
                        )}
                        Import Progress
                    </CardTitle>
                    <Badge variant={statusVariant(job.status)}>
                        {job.status}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                <div>
                    <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                        <span className="text-foreground font-medium">
                            {job.stage}
                        </span>
                        <span className="text-muted-foreground font-mono">
                            {progressPercent}%
                        </span>
                    </div>
                    <div className="bg-muted h-3 overflow-hidden rounded-full">
                        <div
                            className={cn(
                                'h-full rounded-full transition-all duration-500',
                                job.status === 'failed'
                                    ? 'bg-red-600'
                                    : job.status === 'completed'
                                      ? 'bg-green-600'
                                      : 'bg-blue-600',
                            )}
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                    {PROGRESS_STAGES.map((stage, index) => {
                        const isFailedStage =
                            job.status === 'failed' &&
                            index === activeStageIndex;
                        const isDone =
                            job.status === 'completed' ||
                            (isRunning && progressPercent >= stage.threshold) ||
                            (job.status === 'failed' &&
                                index < activeStageIndex);
                        const isCurrent =
                            isRunning && activeStage.label === stage.label;
                        return (
                            <div
                                key={stage.label}
                                className={cn(
                                    'flex min-h-16 flex-col justify-between rounded-md border p-3',
                                    isFailedStage
                                        ? 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/50'
                                        : isDone
                                          ? 'border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/50'
                                          : 'border-border bg-background',
                                )}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-foreground/80 text-xs font-medium">
                                        {stage.label}
                                    </span>
                                    {isFailedStage ? (
                                        <CircleX className="h-4 w-4 text-red-700 dark:text-red-400" />
                                    ) : isDone ? (
                                        <CircleCheck className="h-4 w-4 text-blue-700 dark:text-blue-400" />
                                    ) : (
                                        <span className="border-border h-4 w-4 rounded-full border" />
                                    )}
                                </div>
                                {isCurrent && (
                                    <div className="mt-2 h-1 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-950/50">
                                        <div className="h-full w-2/3 animate-pulse rounded-full bg-blue-600" />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {job.error && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                        {job.error}
                    </div>
                )}
                <div className="grid gap-4 lg:grid-cols-[1fr_240px]">
                    <div>
                        <div className="text-muted-foreground mb-2 text-xs font-medium">
                            Log
                        </div>
                        <ScrollArea
                            className="h-48 rounded-md border bg-gray-950 p-3"
                            forceMountScrollbar={true}
                        >
                            <div className="space-y-1 font-mono text-xs text-gray-100">
                                {job.logs.length === 0 ? (
                                    <div className="text-gray-400">
                                        Waiting for import output…
                                    </div>
                                ) : (
                                    job.logs.map((line, index) => (
                                        <div key={`${line}-${index}`}>
                                            {line}
                                        </div>
                                    ))
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                    <div>
                        <div className="text-muted-foreground mb-2 text-xs font-medium">
                            Result
                        </div>
                        <div className="rounded-md border p-3 text-sm">
                            {job.result ? (
                                <div className="space-y-2">
                                    <div>
                                        <div className="text-muted-foreground text-xs">
                                            Snapshot Org
                                        </div>
                                        <Link
                                            to="/admin/organizations/$organization-id"
                                            params={{
                                                'organization-id':
                                                    job.result
                                                        .snapshot_organization_id,
                                            }}
                                            className="font-mono break-all text-blue-700 hover:underline dark:text-blue-400"
                                        >
                                            {
                                                job.result
                                                    .snapshot_organization_id
                                            }
                                        </Link>
                                    </div>
                                    <div>
                                        <div className="text-muted-foreground text-xs">
                                            Rows
                                        </div>
                                        <div className="font-medium">
                                            {formatCount(
                                                tableCounts.reduce(
                                                    (sum, [, count]) =>
                                                        sum + count,
                                                    0,
                                                ),
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-muted-foreground text-xs">
                                            Safety
                                        </div>
                                        <div className="font-medium text-green-700 dark:text-green-400">
                                            Passed
                                        </div>
                                    </div>
                                    {job.result.skipped_tables.length > 0 && (
                                        <div>
                                            <div className="text-muted-foreground text-xs">
                                                Skipped
                                            </div>
                                            <div className="font-medium text-amber-700 dark:text-amber-400">
                                                {formatCount(
                                                    job.result.skipped_tables
                                                        .length,
                                                )}{' '}
                                                tables
                                            </div>
                                            <div className="text-muted-foreground mt-1 max-h-20 overflow-y-auto font-mono text-xs">
                                                {job.result.skipped_tables.join(
                                                    ', ',
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-muted-foreground">
                                    Pending
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

function OrgSnapshotsPage() {
    const [orgPickerOpen, setOrgPickerOpen] = useState(false);
    const [orgSearch, setOrgSearch] = useState('');
    const [selectedOrganization, setSelectedOrganization] =
        useState<ProdOrganization | null>(null);
    const [snapshotName, setSnapshotName] = useState('');
    const [snapshotKey, setSnapshotKey] = useState('');
    const [activeJobId, setActiveJobId] = useState<string | null>(null);
    const [isStarting, setIsStarting] = useState(false);
    const [isStartingTunnel, setIsStartingTunnel] = useState(false);
    const [tunnelStartRequested, setTunnelStartRequested] = useState(false);

    const {
        data: status,
        isLoading: isLoadingStatus,
        mutate: mutateStatus,
    } = useOrgSnapshotsStatus(activeJobId ? 2500 : 7000);
    const {
        data: prodOrganizations,
        isLoading: isSearching,
        error: searchError,
    } = useProdOrganizationSearch(orgSearch, orgPickerOpen);
    const { data: activeJob, error: activeJobError } = useOrgSnapshotImportJob(
        activeJobId,
        activeJobId ? 1500 : undefined,
    );
    const activeStatusJob = activeJobId
        ? status?.jobs.find((job) => job.id === activeJobId)
        : undefined;
    const activeJobNotFound =
        activeJobError instanceof ApiError && activeJobError.status === 404;
    const readonlyTunnelMissing =
        status?.prod_readonly_url_configured === false;
    const readonlyTunnelStarting = isStartingTunnel || tunnelStartRequested;

    useEffect(() => {
        if (
            !activeJobId ||
            (!activeJobNotFound &&
                !isTerminalImportStatus(activeJob?.status) &&
                !isTerminalImportStatus(activeStatusJob?.status))
        ) {
            return;
        }

        const inactiveJobId = activeJobId;
        void mutateStatus().finally(() => {
            setActiveJobId((currentJobId) =>
                currentJobId === inactiveJobId ? null : currentJobId,
            );
        });
    }, [
        activeJob?.status,
        activeJobId,
        activeJobNotFound,
        activeStatusJob?.status,
        mutateStatus,
    ]);

    useEffect(() => {
        if (status?.prod_readonly_url_configured) {
            setTunnelStartRequested(false);
        }
    }, [status?.prod_readonly_url_configured]);

    useEffect(() => {
        if (
            !tunnelStartRequested ||
            status?.prod_readonly_url_configured === true
        ) {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            setTunnelStartRequested(false);
        }, 30_000);
        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [status?.prod_readonly_url_configured, tunnelStartRequested]);

    const latestJob = activeJobId
        ? (activeJob ?? activeStatusJob)
        : status?.jobs[0];

    const handleSelectOrganization = (organization: ProdOrganization) => {
        setSelectedOrganization(organization);
        setSnapshotName(buildDefaultSnapshotName(organization.name));
        setSnapshotKey('');
        setOrgPickerOpen(false);
    };

    const selectedOrganizationDisplay = selectedOrganization ? (
        <span className="flex min-w-0 flex-col">
            <span className="truncate">{selectedOrganization.name}</span>
            <span className="text-muted-foreground truncate font-mono text-xs">
                {selectedOrganization.internal_name ??
                    selectedOrganization.organization_id}
            </span>
        </span>
    ) : null;

    const handleStartImport = async () => {
        if (!selectedOrganization || snapshotName.trim().length === 0) {
            return;
        }
        setIsStarting(true);
        try {
            const response = await getApiClient().post({
                url: '/admin/org-snapshots/imports',
                body: {
                    source_organization_id:
                        selectedOrganization.organization_id,
                    snapshot_name: snapshotName.trim(),
                    snapshot_key: snapshotKey.trim() || null,
                    profile: 'full-org-local',
                },
                reason: 'admin-org-snapshot-import',
            });
            setActiveJobId(response.job.id);
            await mutateStatus();
            toast.success('Snapshot import started.', {
                persistNotification: false,
            });
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to start snapshot import.';
            toast.error(message, { persistNotification: false });
        } finally {
            setIsStarting(false);
        }
    };

    const handleStartReadonlyTunnel = async () => {
        setIsStartingTunnel(true);
        try {
            const response = await getApiClient().post({
                url: '/admin/org-snapshots/prod-readonly-tunnel',
                reason: 'admin-org-snapshot-start-prod-readonly-tunnel',
            });
            setTunnelStartRequested(response.status === 'starting');
            toast.success(response.message, { persistNotification: false });
            try {
                await mutateStatus();
            } catch {
                toast.error(
                    'Readonly tunnel start requested, but status refresh failed.',
                    { persistNotification: false },
                );
            }
        } catch (error) {
            setTunnelStartRequested(false);
            const message =
                error instanceof Error
                    ? error.message
                    : 'Failed to start production readonly tunnel.';
            toast.error(message, { persistNotification: false });
        } finally {
            setIsStartingTunnel(false);
        }
    };

    return (
        <div className="flex flex-col gap-6 p-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-foreground text-2xl font-semibold">
                        Org Snapshots
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">full-org-local</Badge>
                        <Badge
                            variant={
                                status?.prod_readonly_url_configured
                                    ? 'success'
                                    : 'outline'
                            }
                        >
                            Prod readonly{' '}
                            {status?.prod_readonly_url_configured
                                ? 'connected'
                                : 'missing'}
                        </Badge>
                        <span className="text-muted-foreground font-mono text-xs">
                            fake phones:{' '}
                            {status?.fake_phone_prefix
                                ? `${status.fake_phone_prefix}…`
                                : 'loading'}
                        </span>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {readonlyTunnelMissing && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                void handleStartReadonlyTunnel();
                            }}
                            disabled={readonlyTunnelStarting}
                        >
                            {readonlyTunnelStarting ? (
                                <Spinner className="mr-2" />
                            ) : (
                                <Database className="mr-2 h-4 w-4" />
                            )}
                            {readonlyTunnelStarting
                                ? 'Starting readonly tunnel'
                                : 'Start readonly tunnel'}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={() => {
                            setTunnelStartRequested(false);
                            void mutateStatus();
                        }}
                    >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Refresh
                    </Button>
                </div>
            </div>
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_520px]">
                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Production Organization</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Combobox
                                open={orgPickerOpen}
                                onOpenChange={setOrgPickerOpen}
                                displayValue={selectedOrganizationDisplay}
                                placeholder="Select production organization"
                                searchValue={orgSearch}
                                onSearchValueChange={setOrgSearch}
                                searchPlaceholder="Search name, internal name, or UUID…"
                                triggerClassName="h-12"
                                contentClassName="w-[560px] p-1"
                                commandProps={{ shouldFilter: false }}
                                commandListProps={{
                                    className:
                                        'max-h-80 overflow-y-auto overscroll-contain',
                                    onWheel: (event) => {
                                        event.stopPropagation();
                                        const target = event.currentTarget;
                                        target.scrollTop += event.deltaY;
                                    },
                                }}
                            >
                                <CommandEmpty className="text-muted-foreground py-6 text-center text-sm">
                                    {isSearching
                                        ? 'Loading organizations…'
                                        : searchError
                                          ? searchError.message
                                          : 'No matching organizations.'}
                                </CommandEmpty>
                                <CommandGroup>
                                    {(prodOrganizations ?? []).map(
                                        (organization) => (
                                            <CommandItem
                                                key={
                                                    organization.organization_id
                                                }
                                                value={
                                                    organization.organization_id
                                                }
                                                onSelect={() =>
                                                    handleSelectOrganization(
                                                        organization,
                                                    )
                                                }
                                                className="items-start gap-3 py-3"
                                            >
                                                <Check
                                                    className={cn(
                                                        'mt-0.5 h-4 w-4 shrink-0',
                                                        selectedOrganization?.organization_id ===
                                                            organization.organization_id
                                                            ? 'opacity-100'
                                                            : 'opacity-0',
                                                    )}
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate font-medium">
                                                        {organization.name}
                                                    </div>
                                                    <div className="text-muted-foreground truncate font-mono text-xs">
                                                        {organization.internal_name ??
                                                            organization.organization_id}
                                                    </div>
                                                </div>
                                                <div className="text-muted-foreground shrink-0 text-right text-xs">
                                                    <div>
                                                        {formatCount(
                                                            organization.caregiver_count,
                                                        )}{' '}
                                                        caregivers
                                                    </div>
                                                    <div>
                                                        {formatCount(
                                                            organization.shift_count,
                                                        )}{' '}
                                                        shifts
                                                    </div>
                                                </div>
                                            </CommandItem>
                                        ),
                                    )}
                                </CommandGroup>
                            </Combobox>
                            {searchError && (
                                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300">
                                    {searchError.message}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                    <Card>
                        <CardHeader>
                            <CardTitle>Import</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid gap-4 md:grid-cols-2">
                                <label className="space-y-2">
                                    <span className="text-sm font-medium">
                                        Snapshot name
                                    </span>
                                    <Input
                                        value={snapshotName}
                                        onChange={(event) =>
                                            setSnapshotName(event.target.value)
                                        }
                                        disabled={!selectedOrganization}
                                    />
                                </label>
                                <label className="space-y-2">
                                    <span className="text-sm font-medium">
                                        Snapshot key
                                    </span>
                                    <Input
                                        value={snapshotKey}
                                        onChange={(event) =>
                                            setSnapshotKey(event.target.value)
                                        }
                                        disabled={!selectedOrganization}
                                        placeholder="Auto-generated"
                                    />
                                </label>
                            </div>
                            <div className="bg-muted/50 rounded-md border p-3 text-sm">
                                {selectedOrganization ? (
                                    <Fragment>
                                        <div className="text-foreground font-medium">
                                            {selectedOrganization.name}
                                        </div>
                                        <div className="text-muted-foreground font-mono text-xs">
                                            {
                                                selectedOrganization.organization_id
                                            }
                                        </div>
                                    </Fragment>
                                ) : (
                                    <div className="text-muted-foreground">
                                        Select a production organization.
                                    </div>
                                )}
                            </div>
                            <Button
                                onClick={handleStartImport}
                                disabled={
                                    !selectedOrganization ||
                                    snapshotName.trim().length === 0 ||
                                    isStarting
                                }
                            >
                                {isStarting ? (
                                    <Spinner className="mr-2" />
                                ) : (
                                    <Database className="mr-2 h-4 w-4" />
                                )}
                                Import Snapshot
                            </Button>
                        </CardContent>
                    </Card>
                </div>
                <ImportProgressPanel job={latestJob} />
            </div>
            <Card>
                <CardHeader>
                    <CardTitle>Local Snapshot Orgs</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Snapshot</TableHead>
                                <TableHead>Source</TableHead>
                                <TableHead>Caregivers</TableHead>
                                <TableHead>Shifts</TableHead>
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(status?.snapshots ?? []).map((snapshot) => (
                                <TableRow key={snapshot.organization_id}>
                                    <TableCell>
                                        <div className="font-medium">
                                            {snapshot.name}
                                        </div>
                                        <div className="text-muted-foreground font-mono text-xs">
                                            {snapshot.snapshot_key ??
                                                snapshot.organization_id}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-muted-foreground font-mono text-xs">
                                        {snapshot.source_organization_id ??
                                            'unknown'}
                                    </TableCell>
                                    <TableCell>
                                        {formatCount(snapshot.caregiver_count)}
                                    </TableCell>
                                    <TableCell>
                                        {formatCount(snapshot.shift_count)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            asChild={true}
                                        >
                                            <Link
                                                to="/admin/organizations/$organization-id"
                                                params={{
                                                    'organization-id':
                                                        snapshot.organization_id,
                                                }}
                                            >
                                                Open
                                            </Link>
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!isLoadingStatus &&
                                (status?.snapshots ?? []).length === 0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={5}
                                            className="text-muted-foreground"
                                        >
                                            No local snapshot orgs.
                                        </TableCell>
                                    </TableRow>
                                )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
